import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { PageHeader, ErrorState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, ListChecks, FileText, CalendarDays } from "lucide-react";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
  format, isSameMonth, isSameDay, addMonths, subMonths, parseISO, isToday,
} from "date-fns";
import { cn } from "@/lib/utils";

export default function CalendarPage() {
  const navigate = useNavigate();
  const [month, setMonth] = useState(new Date());
  const [selected, setSelected] = useState(new Date());

  const { data, isError, refetch } = useQuery({ queryKey: ["calendar"], queryFn: async () => (await client.get("/calendar")).data });
  const entries = data || [];

  const byDate = entries.reduce((acc, e) => {
    (acc[e.date] = acc[e.date] || []).push(e);
    return acc;
  }, {});

  const gridStart = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
  const gridEnd = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

  const selKey = format(selected, "yyyy-MM-dd");
  const selectedEntries = (byDate[selKey] || []).sort((a, b) => (b.order_value || 0) - (a.order_value || 0));

  return (
    <div className="animate-fade-up">
      <PageHeader title="Calendar" subtitle="Upcoming follow-ups, showroom visits and booked jobs" testid="calendar-header" />

      {isError ? (
        <ErrorState onRetry={refetch} message="We couldn't load your calendar. Please try again." />
      ) : (
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Month grid */}
        <div className="rounded-2xl border border-border bg-white p-4 shadow-[0_2px_8px_rgba(35,52,90,0.04)] lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display text-lg font-bold text-ink" data-testid="calendar-month-label">{format(month, "MMMM yyyy")}</h2>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" onClick={() => setMonth(subMonths(month, 1))} data-testid="cal-prev" className="h-8 w-8"><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" size="sm" onClick={() => { setMonth(new Date()); setSelected(new Date()); }} data-testid="cal-today">Today</Button>
              <Button variant="outline" size="icon" onClick={() => setMonth(addMonths(month, 1))} data-testid="cal-next" className="h-8 w-8"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>

          <div className="mb-2 grid grid-cols-7 gap-1 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((day) => {
              const key = format(day, "yyyy-MM-dd");
              const dayEntries = byDate[key] || [];
              const inMonth = isSameMonth(day, month);
              const isSel = isSameDay(day, selected);
              const followups = dayEntries.filter((e) => e.type === "followup").length;
              const visits = dayEntries.filter((e) => e.type === "visit").length;
              const jobs = dayEntries.filter((e) => e.type === "job").length;
              return (
                <button
                  key={key}
                  onClick={() => setSelected(day)}
                  data-testid={`cal-day-${key}`}
                  className={cn(
                    "flex min-h-[64px] flex-col rounded-lg border p-1.5 text-left transition-colors",
                    isSel ? "border-primary bg-accent" : "border-transparent hover:border-border hover:bg-muted/50",
                    !inMonth && "opacity-40"
                  )}
                >
                  <span className={cn("text-xs font-semibold", isToday(day) ? "grid h-5 w-5 place-items-center rounded-full bg-primary text-primary-foreground" : "text-ink")}>
                    {format(day, "d")}
                  </span>
                  <div className="mt-auto flex flex-wrap gap-1">
                    {jobs > 0 && <span className="rounded px-1 text-[10px] font-semibold text-[#7C3AED] bg-[#7C3AED]/12">{jobs}·Job</span>}
                    {followups > 0 && <span className="rounded px-1 text-[10px] font-semibold text-[#D97706] bg-[#D97706]/12">{followups}·FU</span>}
                    {visits > 0 && <span className="rounded px-1 text-[10px] font-semibold text-[#0284C7] bg-[#0284C7]/12">{visits}·V</span>}
                  </div>
                </button>
              );
            })}
          </div>
          <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground" data-testid="calendar-legend">
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-[#7C3AED]/50" />Booked job</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-[#D97706]/50" />Follow-up</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-[#0284C7]/50" />Showroom visit</span>
          </div>
        </div>

        {/* Selected day list */}
        <div className="rounded-2xl border border-border bg-white p-5 shadow-[0_2px_8px_rgba(35,52,90,0.04)]">
          <h3 className="font-display text-base font-bold text-ink">{format(selected, "EEEE, d MMM")}</h3>
          <p className="mb-4 text-xs text-muted-foreground">{selectedEntries.length} {selectedEntries.length === 1 ? "item" : "items"} scheduled</p>
          {selectedEntries.length === 0 ? (
            <div className="flex flex-col items-center py-10 text-center">
              <CalendarDays className="mb-2 h-6 w-6 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Nothing scheduled for this day.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {selectedEntries.map((e, i) => (
                <button key={i} onClick={() => navigate(`/customers/${e.customer_id}`)} data-testid={`cal-entry-${i}`}
                  className="flex w-full items-center gap-3 rounded-lg border border-border p-3 text-left transition-colors hover:border-primary/40 hover:bg-muted/40">
                  <div className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-lg", e.type === "followup" ? "bg-[#D97706]/12 text-[#D97706]" : e.type === "job" ? "bg-[#7C3AED]/12 text-[#7C3AED]" : "bg-[#0284C7]/12 text-[#0284C7]")}>
                    {e.type === "followup" ? <ListChecks className="h-4 w-4" /> : e.type === "job" ? <CalendarDays className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-ink">{e.customer_name}</div>
                    <div className="text-xs text-muted-foreground">{e.type === "followup" ? "Follow-up" : e.type === "job" ? "Booked job" : "Showroom visit"}{e.company ? ` · ${e.company}` : ""}</div>
                  </div>
                  {e.order_value > 0 && <div className="font-display text-sm font-bold text-ink">{money(e.order_value)}</div>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
